"use client";

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Map as MapIcon, MapPin, Church, Landmark, Building2, LandPlot, X } from 'lucide-react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { sanitizeFirestoreData, cn } from '@/lib/utils';
import type { CityMapConfig, CityMapPin, CityMapPinCategory, CitySecondaryMap } from '@/lib/types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

function categoryIcon(c: CityMapPinCategory) {
  switch (c) {
    case 'iglesia': return Church;
    case 'catedral': return Landmark;
    case 'zona': return LandPlot;
    case 'edificio': return Building2;
    default: return MapPin;
  }
}
const CATEGORY_LABEL: Record<CityMapPinCategory, string> = {
  iglesia: 'Iglesia', catedral: 'Catedral', zona: 'Zona', edificio: 'Edificio',
};

export function CityMapSection() {
  const [mainConfig, setMainConfig] = useState<CityMapConfig | null>(null);
  const [secondaryMaps, setSecondaryMaps] = useState<CitySecondaryMap[]>([]);
  const [allPins, setAllPins] = useState<CityMapPin[]>([]);
  const [activeMapId, setActiveMapId] = useState<string>('main');
  const [selectedPin, setSelectedPin] = useState<CityMapPin | null>(null);

  useEffect(() => {
    const unsubMain = onSnapshot(doc(db, 'city_map_config', 'main'), (snap) => {
      setMainConfig(snap.exists() ? (sanitizeFirestoreData({ id: snap.id, ...snap.data() }) as CityMapConfig) : null);
    });
    const unsubSecondary = onSnapshot(collection(db, 'city_maps'), (snap) => {
      setSecondaryMaps(snap.docs.map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() })) as CitySecondaryMap[]);
    });
    const unsubPins = onSnapshot(collection(db, 'city_map_pins'), (snap) => {
      setAllPins(snap.docs.map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() })) as CityMapPin[]);
    });
    return () => { unsubMain(); unsubSecondary(); unsubPins(); };
  }, []);

  const isMain = activeMapId === 'main';
  const activeSecondary = isMain ? null : secondaryMaps.find((m) => m.id === activeMapId) || null;
  const activeImageUrl = isMain ? mainConfig?.mapImageUrl : activeSecondary?.mapImageUrl;
  const activeName = isMain ? (mainConfig?.name || 'Jartiland') : (activeSecondary?.name || '');
  const activeDescription = isMain
    ? (mainConfig?.description || 'Recorre el mapa de la ciudad y descubre sus iglesias, catedrales, zonas y edificios más emblemáticos.')
    : (activeSecondary?.description || '');

  const pins = useMemo(() => allPins.filter((p) => (p.mapId || 'main') === activeMapId), [allPins, activeMapId]);

  if (!activeImageUrl && secondaryMaps.length === 0 && !mainConfig?.mapImageUrl) {
    return null; // nada que mostrar todavía
  }

  return (
    <section className="relative w-full overflow-hidden bg-background/85 py-24 md:py-32">
      <div className="absolute -top-24 left-0 h-96 w-96 rounded-full bg-primary/10 blur-[120px]" />
      <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-primary/5 blur-[100px]" />

      <div className="container relative mx-auto px-4">
        <div className="mx-auto max-w-3xl text-center">
          <motion.div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/30 bg-primary/10"
            initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.4 }}
          >
            <MapIcon className="h-7 w-7 text-primary" />
          </motion.div>
          <motion.h2
            className="font-headline text-4xl font-bold md:text-5xl"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.5 }}
          >
            Explora <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent">Jartiland</span>
          </motion.h2>
          <motion.p
            className="mt-4 text-lg text-muted-foreground"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.5, delay: 0.1 }}
          >
            {activeDescription}
          </motion.p>
        </div>

        {/* Selector de mapas */}
        {(secondaryMaps.length > 0) && (
          <div className="mx-auto mt-8 flex max-w-5xl flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => { setActiveMapId('main'); setSelectedPin(null); }}
              className={cn('flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors',
                isMain ? 'border-primary/50 bg-primary/15 text-primary' : 'border-white/10 hover:bg-white/5')}
            >
              <MapIcon className="h-4 w-4" /> {mainConfig?.name || 'Mapa principal'}
            </button>
            {secondaryMaps.map((m) => (
              <button
                key={m.id}
                onClick={() => { setActiveMapId(m.id); setSelectedPin(null); }}
                className={cn('flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors',
                  activeMapId === m.id ? 'border-primary/50 bg-primary/15 text-primary' : 'border-white/10 hover:bg-white/5')}
              >
                <MapIcon className="h-4 w-4" /> {m.name || 'Mapa'}
              </button>
            ))}
          </div>
        )}

        {activeName && <p className="mt-6 text-center font-headline text-2xl font-bold">{activeName}</p>}

        <motion.div
          key={activeMapId}
          className="relative mx-auto mt-6 w-full max-w-5xl overflow-hidden rounded-2xl border border-white/8 shadow-[0_8px_40px_rgba(0,0,0,0.4)]"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6 }}
        >
          {activeImageUrl ? (
            <div className="relative">
              <img src={activeImageUrl} alt={activeName} className="block w-full" draggable={false} />
              {pins.map((pin) => {
                const Icon = categoryIcon(pin.category);
                return (
                  <button
                    key={pin.id}
                    onClick={() => setSelectedPin(pin)}
                    className="group absolute z-10 flex h-8 w-8 -translate-x-1/2 -translate-y-full items-center justify-center rounded-full border-2 border-white bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-125"
                    style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                    title={pin.name}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex aspect-[16/10] items-center justify-center text-muted-foreground">Este mapa todavía no tiene imagen.</div>
          )}
        </motion.div>
      </div>

      {/* Detalle de chincheta */}
      <Dialog open={!!selectedPin} onOpenChange={(o) => !o && setSelectedPin(null)}>
        <DialogContent className="sm:max-w-lg">
          {selectedPin && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => { const Icon = categoryIcon(selectedPin.category); return <Icon className="h-5 w-5 text-primary" />; })()}
                  {selectedPin.name}
                </DialogTitle>
                <DialogDescription>{CATEGORY_LABEL[selectedPin.category]}</DialogDescription>
              </DialogHeader>
              {selectedPin.images && selectedPin.images.length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {selectedPin.images.map((url) => (
                    <img key={url} src={url} alt="" className="aspect-video w-full rounded-lg object-cover" />
                  ))}
                </div>
              )}
              {selectedPin.description && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedPin.description}</p>}
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
